/**
 * fleet-section-forms — the rental settings of one fleet car (pobočka, tarif, kategorie, note), shared by
 * the full-page enrolment surface (`FleetEnrollForm`) and the edit modal (`FleetEditModal`), like
 * `tarif-section-forms`. Renders `<form id={FLEET_FORM_ID}>` with fields only; the chrome supplies the
 * submit button through the HTML `form` attribute.
 */

import type { FormEvent } from 'react';
import { Checkbox } from '@/components/Checkbox';
import { Field, Fields } from '@/components/Field';
import { SelectInput } from '@/components/SelectInput';
import { Textarea } from '@/components/Textarea';
import { TextInput } from '@/components/TextInput';
import { useSeededSlice, type BodyProps } from '@/modules/vozidla/vozidlo-section-forms';
import { useActiveFirmaId } from '@/modules/skupiny/otherFirma';
import type { FleetEntry, FleetUpdateBody } from './api';
import { cislo } from './cenik-helpers';
import { cenyZobrazeni } from './ceny-helpers';
import { specChybi, specShrnuti } from './spec-helpers';

export const FLEET_FORM_ID = 'pujcovna-vozidlo-form';

/** Selects hold ids as strings; '' = none (tarif, kategorie). */
export interface FleetValue {
  id_pobocky: string;
  id_tarifu: string;
  id_kategorie: string;
  poznamka: string;
  /** Pricing terms as typed; '' = not set. */
  cena_min_den: string;
  km_povoleno_den: string;
  cena_km: string;
  kauce: string;
  /** Where the car is offered besides the rental's own reservations (both optional, independent of each other). */
  servis_nahradni: boolean;
  verejne: boolean;
}

/** What keeps a car that is switched on for the web off it (the API's `web_chybi`), in words. */
export const WEB_CHYBI: Record<FleetEntry['web_chybi'][number], string> = {
  adresa_pobocky: 'pobočka nemá veřejnou adresu (odkaz) pro web',
  otviraci_doba: 'pobočka nemá otevírací dobu',
  platebni_brana: 'majitel vozu nemá použitelnou platební bránu',
  vazba: 'záznam vozu ve skladu už není jeho platný záznam (duplicitní nebo zastaralý VIN)',
  cena: 'vůz nemá denní cenu (tarif, ceník, prodejní cena)',
};

const text = (n: number | null | undefined): string => (n == null ? '' : String(n));

export function fleetSeed(entry?: FleetEntry | null, defaultPobockaId?: number | null): FleetValue {
  return {
    id_pobocky: entry ? String(entry.id_pobocky) : defaultPobockaId != null ? String(defaultPobockaId) : '',
    id_tarifu: entry?.id_tarifu != null ? String(entry.id_tarifu) : '',
    id_kategorie: entry?.id_kategorie != null ? String(entry.id_kategorie) : '',
    poznamka: entry?.poznamka ?? '',
    cena_min_den: text(entry?.cena_min_den),
    km_povoleno_den: text(entry?.km_povoleno_den),
    cena_km: text(entry?.cena_km),
    kauce: text(entry?.kauce),
    servis_nahradni: entry?.servis_nahradni ?? false,
    verejne: entry?.verejne ?? false,
  };
}

const CENOVA_POLE = [
  ['cena_min_den', 'Minimální cena za den'],
  ['km_povoleno_den', 'Povolený nájezd za den'],
  ['cena_km', 'Cena za km navíc'],
  ['kauce', 'Kauce'],
] as const;

export type FleetBodyVysledek = { ok: true; body: FleetUpdateBody & { id_pobocky: number } } | { ok: false; chyba: string };

/**
 * A pobočka is required; an empty tarif / kategorie is sent as `null` so an edit can clear them. The pricing terms travel SLICE-ONLY
 * on an edit (`original` given): a term is sent only when it differs from the entry the form was opened on, so saving a new note never
 * writes back a stale price another admin changed meanwhile; an empty box is `null` (cleared). On enrolment only the filled ones go.
 * A box that is not a number is named instead of sent. The two "offered in" marks travel the same way: on an edit only when changed, on
 * enrolment only when ticked.
 */
export function fleetToBody(v: FleetValue, original?: FleetEntry | null): FleetBodyVysledek {
  const body: FleetUpdateBody & { id_pobocky: number } = {
    id_pobocky: Number(v.id_pobocky),
    id_tarifu: v.id_tarifu === '' ? null : Number(v.id_tarifu),
    id_kategorie: v.id_kategorie === '' ? null : Number(v.id_kategorie),
    poznamka: v.poznamka.trim(),
  };
  for (const klic of ['servis_nahradni', 'verejne'] as const) {
    if (original ? v[klic] !== (original[klic] ?? false) : v[klic]) body[klic] = v[klic];
  }
  for (const [klic, popisek] of CENOVA_POLE) {
    const n = cislo(v[klic]);
    if (n !== null && Number.isNaN(n)) return { ok: false, chyba: `${popisek}: „${v[klic]}“ není číslo.` };
    if (original ? n !== (original[klic] ?? null) : n !== null) body[klic] = n;
  }
  return { ok: true, body };
}

interface Named {
  id: number;
  nazev: string;
}

interface FleetFormBodyProps extends BodyProps<FleetValue> {
  /** The entry being edited: its computed daily prices are shown read-only under the terms. */
  entry?: FleetEntry | null;
  /** `pujcovna:admin`: the pricing terms are pricing setup (the server refuses them below it), so they are read-only for everyone else. */
  canAdmin?: boolean;
  pobocky: Named[];
  tarify: Named[];
  kategorie: Named[];
}

/**
 * The option list with the entry's own current choice guaranteed present. A cached list can be up to 30 s
 * behind a pobočka / tarif created elsewhere; a controlled select whose value has no option shows blank
 * while the form still holds the id, and a save would keep it unseen.
 */
export function withCurrent(list: Named[], id: number | null | undefined, nazev: string | null | undefined): Named[] {
  if (id == null || list.some((x) => x.id === id)) return list;
  return [...list, { id, nazev: nazev || `#${id}` }];
}

export function FleetFormBody({ initial, busy, onSubmit, onChange, entry, canAdmin = false, pobocky, tarify, kategorie }: FleetFormBodyProps) {
  const { form, set } = useSeededSlice(initial, onChange);
  const aktivniFirma = useActiveFirmaId();
  // The payload is captured on submit and both surfaces leave on success (the modal closes, the enrolment
  // page navigates away): an edit typed during a slow save would be silently dropped, so the controls
  // freeze while it runs (`busy`).

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.(form);
  };

  return (
    <form id={FLEET_FORM_ID} onSubmit={submit}>
      <Fields cols={2}>
        <Field label="Pobočka" required>
          <SelectInput
            value={form.id_pobocky}
            required
            disabled={busy}
            data-testid="fleet-pobocka"
            onChange={(e) => set('id_pobocky', e.currentTarget.value)}
          >
            <option value="">—</option>
            {pobocky.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nazev}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Tarif" optional="volitelné">
          <SelectInput value={form.id_tarifu} disabled={busy} onChange={(e) => set('id_tarifu', e.currentTarget.value)}>
            <option value="">Bez tarifu</option>
            {tarify.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nazev}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Kategorie na webu" optional="volitelné">
          <SelectInput value={form.id_kategorie} disabled={busy} onChange={(e) => set('id_kategorie', e.currentTarget.value)}>
            <option value="">Bez kategorie</option>
            {kategorie.map((k) => (
              <option key={k.id} value={k.id}>
                {k.nazev}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field
          label="Kde se vůz nabízí"
          optional="volitelné"
          span
          hint="V půjčovně lze vůz vždy rezervovat ručně. Tyto dvě volby ho navíc nabídnou dalším místům; jsou na sobě nezávislé. Na web se vůz dá zveřejnit po zařazení, v úpravě vozu."
        >
          <div className="flex flex-col gap-2">
            <Checkbox
              label="Náhradní vozidlo v servisu"
              checked={form.servis_nahradni}
              disabled={busy}
              data-testid="fleet-servis-nahradni"
              onChange={() => set('servis_nahradni', !form.servis_nahradni)}
            />
            {/* the web mark is the admin's and belongs to an existing entry (its owner's gateway and what is missing are known there) */}
            {entry && (
              <Checkbox
                label="Zveřejnit na webu půjčovny"
                checked={form.verejne}
                disabled={busy || !canAdmin}
                data-testid="fleet-verejne"
                onChange={() => set('verejne', !form.verejne)}
              />
            )}
            {entry && !canAdmin && <p className="text-[12px] text-ink-3">Vůz na webu zveřejňuje nebo stahuje správce půjčovny.</p>}
            {form.verejne && entry?.verejne && entry.web_chybi.length > 0 && (
              <p data-testid="fleet-web-chybi" className="text-[12px] text-warn">
                Na web se zatím nedostane: {entry.web_chybi.map((c) => WEB_CHYBI[c]).join(', ')}.
              </p>
            )}
          </div>
        </Field>
        <Field
          label="Poznámka"
          optional="volitelné"
          span
          hint="Interní poznámka k vozu v půjčovně, na webu se nezobrazuje."
        >
          <Textarea
            value={form.poznamka}
            maxLength={500}
            rows={3}
            disabled={busy}
            onChange={(e) => set('poznamka', e.currentTarget.value)}
          />
        </Field>
      </Fields>

      {entry?.vozidlo && (
        <section data-testid="fleet-spec-blok" className="mt-8 flex flex-col gap-2 border-t border-line pt-6">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Údaje pro web (z vozu)</div>
          <div className="text-[13px] text-ink-2">
            {specShrnuti(entry.vozidlo.spec) ?? 'Z textu vozu nic nevyplývá.'}
          </div>
          <p className="text-[12px] text-ink-3">
            {specChybi(entry.vozidlo.spec).length > 0
              ? `Chybí: ${specChybi(entry.vozidlo.spec).join(', ')}. `
              : ''}
            Pohon, výkon a převodovku web odvozuje z motoru a převodovky vozu ve skladu; co z textu nevyplývá, web neukáže.{' '}
            {aktivniFirma != null && entry.id_firmy_vlastnika !== aktivniFirma
              ? `Vůz patří firmě ${entry.firma_vlastnika_nazev}: text motoru a převodovky u vozu ve skladu opraví její zaměstnanci.`
              : 'Opravte je u vozu ve skladu.'}
          </p>
        </section>
      )}

      <section data-testid="fleet-ceny" className="mt-8 flex flex-col gap-4 border-t border-line pt-6">
        <div>
          <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Ceny a podmínky</div>
          <p className="mt-1 text-[12px] text-ink-3">
            Denní cena se počítá z prodejní ceny vozu ve skladu, ceníku tarifu a cenových skupin firmy. Zde se nastavují jen podmínky vozu.
            {!canAdmin && ' Podmínky vozu mění správce půjčovny.'}
          </p>
        </div>
        <Fields cols={2}>
          <Field label="Minimální cena za den" optional="volitelné" hint="Pod tuto cenu nejde žádná skupina, délka ani sezóna.">
            <TextInput inputMode="numeric" value={form.cena_min_den} suffix="Kč" disabled={busy || !canAdmin} data-testid="fleet-cena-min" onChange={(e) => set('cena_min_den', e.currentTarget.value)} />
          </Field>
          <Field label="Kauce" optional="volitelné">
            <TextInput inputMode="numeric" value={form.kauce} suffix="Kč" disabled={busy || !canAdmin} data-testid="fleet-kauce" onChange={(e) => set('kauce', e.currentTarget.value)} />
          </Field>
          <Field label="Povolený nájezd za den" optional="volitelné">
            <TextInput inputMode="numeric" value={form.km_povoleno_den} suffix="km" disabled={busy || !canAdmin} data-testid="fleet-km-den" onChange={(e) => set('km_povoleno_den', e.currentTarget.value)} />
          </Field>
          <Field label="Cena za km navíc" optional="volitelné" hint="Za kilometry nad povolený nájezd, např. 4,5.">
            <TextInput inputMode="decimal" value={form.cena_km} suffix="Kč" disabled={busy || !canAdmin} data-testid="fleet-cena-km" onChange={(e) => set('cena_km', e.currentTarget.value)} />
          </Field>
        </Fields>
        {entry && (
          <div data-testid="fleet-ceny-vypocet" className="text-[13px] text-ink-2">
            {(() => {
              const c = cenyZobrazeni(entry);
              return c.ok ? (
                <>
                  Denní cena pro hosta <span className="font-mono text-ink">{c.hlavni}</span> <span className="text-ink-3">· {c.detail}</span>
                </>
              ) : (
                <>Denní cena: {c.chyba}.</>
              );
            })()}
          </div>
        )}
      </section>
    </form>
  );
}
